import {
  ActivationAndFlightPlanSelectors,
  ActivationAndFlightPlansStateAction,
  useActivationAndFlightPlansState,
} from '@components/cockpit/panels/flightPlan/useFlightPlansState';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole, selectActiveUserLogin } from '@redux/authent/authent.selectors';
import { useGetActivationsQuery } from '@services/utm/activation.api';
import { isEqual } from 'lodash';
import {
  ActionDispatch,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import { RoleEnum } from '@/types/authent/roles.types';
import { DateTimeRange } from '@/types/dateTime.types';
import {
  AbstractFlightPlan,
  ActivationAndFlightPlan,
  ActivationCriteria,
  ActivationStatusEnum,
} from '@/types/utm/flightPlan.types';

export type ViewType = 'mixed' | 'list';

interface ActivationAndFlightPlanFilters {
  status: Set<ActivationStatusEnum>;
  range: DateTimeRange;
  conflictStates: Set<'with' | 'without'>;
}

interface FlightPlansManagementContextType {
  isLoadingGetAll: boolean;
  dispatch: ActionDispatch<[action: ActivationAndFlightPlansStateAction]>;
  activationAndFlightPlans: ActivationAndFlightPlan[];
  canCreateFlightPlan: boolean;
  canEditFlightPlan: (activationAndFlightPlan: ActivationAndFlightPlan) => boolean;
  canDeleteFlightPlan: (activationAndFlightPlan: ActivationAndFlightPlan) => boolean;
  canValidateFlightPlan: boolean;
  filters: ActivationAndFlightPlanFilters;
  setFilters: ActionDispatch<[newFilters: Partial<ActivationAndFlightPlanFilters>]>;
  criteria: ActivationCriteria;
  canReset: boolean;
  resetFilters: () => void;
  editedFlightPlanCode: string | null | undefined;
  setEditedFlightPlanCode: (value: string | null | undefined) => void;
  view: ViewType;
  setView: (value: ViewType) => void;
  selectedForDeletionFlightPlan: { name: string; id: number } | null;
  setSelectedForDeletionFlightPlan: (value: { name: string; id: number } | null) => void;
  selectedActivationAndFlightPlanActivationId: number | null;
  setSelectedActivationAndFlightPlanActivationId: (value: number | null) => void;
}

const FlightPlansManagementContext = createContext<FlightPlansManagementContextType | undefined>(undefined);

function getDefaultRange() {
  return {
    from: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    to: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000),
  };
}

export const FlightPlansManagementContextProvider = ({ children }: Readonly<PropsWithChildren>) => {
  const [editedFlightPlanCode, setEditedFlightPlanCode] = useState<string | null | undefined>(null);
  const { state, dispatch } = useActivationAndFlightPlansState();
  const [defaultFilters, setDefaultFilters] = useState<ActivationAndFlightPlanFilters>({
    status: new Set(),
    range: getDefaultRange(),
    conflictStates: new Set(),
  });
  const [filters, setFilters] = useReducer(
    (filters: ActivationAndFlightPlanFilters, newFilters: Partial<ActivationAndFlightPlanFilters>) => ({
      ...filters,
      ...newFilters,
    }),
    { ...defaultFilters },
  );

  const criteria: ActivationCriteria = useMemo(
    () => ({
      from: filters.range.from.toISOString(),
      to: filters.range.to.toISOString(),
      statuses: Array.from(filters.status),
      withConflicts: filters.conflictStates.size === 1 ? filters.conflictStates.has('with') : undefined,
    }),
    [filters.conflictStates, filters.range.from, filters.range.to, filters.status],
  );

  const [selectedForDeletionFlightPlan, setSelectedForDeletionFlightPlan] = useState<{
    name: string;
    id: number;
  } | null>(null);
  const [selectedActivationAndFlightPlanActivationId, setSelectedActivationAndFlightPlanActivationId] = useState<
    number | null
  >(null);
  const [view, setView] = useState<ViewType>('list');

  const activationAndFlightPlans = ActivationAndFlightPlanSelectors.selectAll(state);

  const { data, isLoading: isLoadingGetAll } = useGetActivationsQuery(criteria, {
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data) {
      dispatch({
        type: 'SET_ALL',
        payload: data,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const canCreateFlightPlan = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_FLIGHT_PLAN));
  const canEditFlightPlan_ = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_FLIGHT_PLAN));
  const canDeleteFlightPlan_ = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_FLIGHT_PLAN));
  const canValidateFlightPlan = useAppSelector((state) => hasRole(state, RoleEnum.VALIDATE_FLIGHT_PLAN));
  const login = useAppSelector(selectActiveUserLogin);

  const canEditFlightPlan = useCallback(
    (activationAndFlightPlan: ActivationAndFlightPlan) =>
      canEditFlightPlan_ || (canCreateFlightPlan && activationAndFlightPlan.flightPlan.createdBy === login),
    [canCreateFlightPlan, canEditFlightPlan_, login],
  );

  const canDeleteFlightPlan = useCallback(
    (activationAndFlightPlan: ActivationAndFlightPlan) =>
      canDeleteFlightPlan_ ||
      ((canCreateFlightPlan || canEditFlightPlan_) && activationAndFlightPlan.flightPlan.createdBy === login),
    [canCreateFlightPlan, canDeleteFlightPlan_, canEditFlightPlan_, login],
  );

  useEffect(() => {
    if (
      !activationAndFlightPlans.some(
        (activationAndFlightPlan) =>
          activationAndFlightPlan.activation.id === selectedActivationAndFlightPlanActivationId,
      )
    ) {
      setSelectedActivationAndFlightPlanActivationId(null);
    }
  }, [activationAndFlightPlans, selectedActivationAndFlightPlanActivationId]);

  const resetFilters = useCallback(() => {
    const newDefault = {
      ...defaultFilters,
      range: getDefaultRange(),
    };
    setFilters(newDefault);
    setDefaultFilters(newDefault);
  }, [defaultFilters]);

  const canReset = !isEqual(filters, defaultFilters);

  const values = useMemo(
    () => ({
      isLoadingGetAll,
      dispatch,
      activationAndFlightPlans,
      canCreateFlightPlan,
      canEditFlightPlan,
      canDeleteFlightPlan,
      canValidateFlightPlan,
      editedFlightPlanCode,
      setEditedFlightPlanCode,
      filters,
      setFilters,
      canReset,
      resetFilters,
      criteria,
      view,
      setView,
      selectedForDeletionFlightPlan,
      setSelectedForDeletionFlightPlan,
      selectedActivationAndFlightPlanActivationId,
      setSelectedActivationAndFlightPlanActivationId,
    }),
    [
      isLoadingGetAll,
      dispatch,
      activationAndFlightPlans,
      canCreateFlightPlan,
      canEditFlightPlan,
      canDeleteFlightPlan,
      canValidateFlightPlan,
      editedFlightPlanCode,
      filters,
      canReset,
      resetFilters,
      criteria,
      view,
      selectedForDeletionFlightPlan,
      selectedActivationAndFlightPlanActivationId,
    ],
  );

  return <FlightPlansManagementContext value={values}>{children}</FlightPlansManagementContext>;
};

export const useFlightPlansManagementContext = (): FlightPlansManagementContextType => {
  const context = useContext<FlightPlansManagementContextType | undefined>(FlightPlansManagementContext);
  if (!context) {
    throw new Error('useFlightPlansManagementContext must be used within a FlightPlansManagementContext.Provider');
  }
  return context;
};

export const useActivationAndFlightPlanByActivationId = (
  id: number | null | undefined,
): ActivationAndFlightPlan | undefined => {
  const { activationAndFlightPlans } = useFlightPlansManagementContext();

  return useMemo(
    () => activationAndFlightPlans.find((activationAndFlightPlan) => activationAndFlightPlan.activation.id === id),
    [activationAndFlightPlans, id],
  );
};

export const useFlightPlanByCode = (code: string | null | undefined): AbstractFlightPlan | undefined => {
  const { activationAndFlightPlans } = useFlightPlansManagementContext();

  return useMemo(
    () =>
      activationAndFlightPlans.find((activationAndFlightPlan) => activationAndFlightPlan.flightPlan.code === code)
        ?.flightPlan,
    [activationAndFlightPlans, code],
  );
};
